import React, { useState, useEffect, useCallback } from 'react';
import { AdminLayout } from '../layouts';
import {
  FaSearch,
  FaFilter,
  FaEdit,
  FaTrash,
  FaEye,
  FaPlus,
  FaCalendarAlt,
  FaClock,
} from 'react-icons/fa';
import { API_BASE, authFetch, UPLOADS_BASE } from '@/services/api';
import { AddEventModal } from './AddEventModal';
import { ViewEventModal } from './ViewEventModal';
import type { EventRecord as ViewEventRecord } from './ViewEventModal';
import './Events.css';

interface EventRecord {
  id: number;
  title: string;
  short_description: string;
  host: string;
  is_one_day: number;
  start_date: string;
  start_time: string;
  end_date: string | null;
  end_time: string | null;
  location: string;
  is_free: number;
  full_details: string | null;
  feature_image: string | null;
  agenda_file: string | null;
  duration: string | null;
  requirements: string | null;
  status: 'upcoming' | 'ongoing' | 'completed' | 'cancelled';
  created_at: string;
  updated_at: string | null;
}

function getUploadUrl(path: string | null): string {
  if (!path) return '';
  return path.startsWith('http') ? path : UPLOADS_BASE + path.replace(/^\//, '');
}

async function fetchEventsList(params: { status?: string; search?: string }): Promise<EventRecord[]> {
  const sp = new URLSearchParams();
  if (params.status) sp.set('status', params.status);
  if (params.search) sp.set('search', params.search);
  const q = sp.toString();
  const url = API_BASE + 'events/list' + (q ? '&' + q : '');
  const res = await authFetch(url);
  const json = await res.json();
  if (!res.ok) throw new Error(json?.message || 'Failed to fetch events');
  if (!json.success || !Array.isArray(json.data)) throw new Error(json?.message || 'Invalid response');
  return json.data;
}

async function deleteEventById(id: number): Promise<void> {
  const url = `${API_BASE}events/delete&id=${id}`;
  const res = await authFetch(url, { method: 'DELETE' });
  const json = await res.json();
  if (!res.ok) throw new Error(json?.message || 'Failed to delete event');
  if (!json.success) throw new Error(json?.message || 'Failed to delete event');
}

export const Events: React.FC = () => {
  const [events, setEvents] = useState<EventRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [viewEvent, setViewEvent] = useState<EventRecord | null>(null);
  const [editEvent, setEditEvent] = useState<EventRecord | null>(null);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: { status?: string; search?: string } = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      if (searchTerm.trim()) params.search = searchTerm.trim();
      const data = await fetchEventsList(params);
      setEvents(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load events');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, searchTerm]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleEventCreatedOrUpdated = useCallback(() => {
    setIsAddModalOpen(false);
    setEditEvent(null);
    fetchEvents();
  }, [fetchEvents]);

  const handleDelete = useCallback(
    async (id: number) => {
      if (!window.confirm('Are you sure you want to delete this event?')) return;
      try {
        await deleteEventById(id);
        fetchEvents();
      } catch (e) {
        alert(e instanceof Error ? e.message : 'Failed to delete event');
      }
    },
    [fetchEvents]
  );

  const filteredEvents = events.filter((event) => {
    const matchesSearch =
      !searchTerm ||
      event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      event.short_description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      event.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
      event.host.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || event.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'upcoming':
        return 'admin-events__status--upcoming';
      case 'ongoing':
        return 'admin-events__status--ongoing';
      case 'completed':
        return 'admin-events__status--completed';
      case 'cancelled':
        return 'admin-events__status--cancelled';
      default:
        return '';
    }
  };

  const formatTime = (t: string | null) => (t ? t.slice(0, 5) : '');

  return (
    <AdminLayout>
      <div className="admin-events">
        <div className="admin-events__header">
          <div>
            <h1 className="admin-events__title">Events Management</h1>
            <p className="admin-events__subtitle">Plan and coordinate upcoming Franciscan events</p>
          </div>
          <button
            type="button"
            className="admin-events__add-btn"
            onClick={() => setIsAddModalOpen(true)}
          >
            <FaPlus />
            <span>Create New Event</span>
          </button>
        </div>

        {error && (
          <div className="admin-events__error" role="alert">
            {error}
          </div>
        )}

        {/* Quick Stats */}
        <div className="admin-events__stats">
          <div className="admin-events__stat-card">
            <div className="admin-events__stat-icon admin-events__stat-icon--total">
              <FaCalendarAlt />
            </div>
            <div className="admin-events__stat-info">
              <span className="admin-events__stat-label">Total Events</span>
              <span className="admin-events__stat-value">{events.length}</span>
            </div>
          </div>
          <div className="admin-events__stat-card">
            <div className="admin-events__stat-icon admin-events__stat-icon--upcoming">
              <FaClock />
            </div>
            <div className="admin-events__stat-info">
              <span className="admin-events__stat-label">Upcoming</span>
              <span className="admin-events__stat-value">{events.filter((e) => e.status === 'upcoming').length}</span>
            </div>
          </div>
          <div className="admin-events__stat-card">
            <div className="admin-events__stat-icon admin-events__stat-icon--ongoing">
              <FaCalendarAlt />
            </div>
            <div className="admin-events__stat-info">
              <span className="admin-events__stat-label">Ongoing Today</span>
              <span className="admin-events__stat-value">{events.filter((e) => e.status === 'ongoing').length}</span>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="admin-events__filters">
          <div className="admin-events__search">
            <FaSearch className="admin-events__search-icon" />
            <input
              type="text"
              placeholder="Search events..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="admin-events__search-input"
            />
          </div>
          <div className="admin-events__filter-group">
            <FaFilter />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="admin-events__filter-select"
            >
              <option value="all">All Status</option>
              <option value="upcoming">Upcoming</option>
              <option value="ongoing">Ongoing</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>

        {/* Events List (table) */}
        <div className="admin-events__table-wrap">
          {loading ? (
            <div className="admin-events__empty">
              <p>Loading events…</p>
            </div>
          ) : filteredEvents.length > 0 ? (
            <table className="admin-events__table">
              <thead>
                <tr>
                  <th className="admin-events__th-image">Image</th>
                  <th className="admin-events__th-title">Title</th>
                  <th className="admin-events__th-date">Date</th>
                  <th className="admin-events__th-location">Location</th>
                  <th className="admin-events__th-host">Host</th>
                  <th className="admin-events__th-status">Status</th>
                  <th className="admin-events__th-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredEvents.map((event) => (
                  <tr key={event.id} className="admin-events__row">
                    <td className="admin-events__td-image">
                      {event.feature_image ? (
                        <img src={getUploadUrl(event.feature_image)} alt="" className="admin-events__thumb" />
                      ) : (
                        <span className="admin-events__no-image">—</span>
                      )}
                    </td>
                    <td className="admin-events__td-title">
                      <strong>{event.title}</strong>
                      {event.short_description && (
                        <div className="admin-events__row-desc">{event.short_description.slice(0, 80)}{event.short_description.length > 80 ? '…' : ''}</div>
                      )}
                    </td>
                    <td className="admin-events__td-date">
                      {new Date(event.start_date).toLocaleDateString()}
                      {event.end_date && event.end_date !== event.start_date && (
                        <div className="admin-events__row-meta">to {new Date(event.end_date).toLocaleDateString()}</div>
                      )}
                      <div className="admin-events__row-meta">{formatTime(event.start_time)}{event.end_time ? ` – ${formatTime(event.end_time)}` : ''}</div>
                    </td>
                    <td className="admin-events__td-location">{event.location}</td>
                    <td className="admin-events__td-host">{event.host}</td>
                    <td className="admin-events__td-status">
                      <span className={`admin-events__status ${getStatusBadgeClass(event.status)}`}>
                        {event.status.charAt(0).toUpperCase() + event.status.slice(1)}
                      </span>
                    </td>
                    <td className="admin-events__td-actions">
                      <button
                        type="button"
                        className="admin-events__action-btn"
                        title="View"
                        aria-label="View"
                        onClick={() => setViewEvent(event)}
                      >
                        <FaEye />
                      </button>
                      <button
                        type="button"
                        className="admin-events__action-btn"
                        title="Edit"
                        aria-label="Edit"
                        onClick={() => {
                          setEditEvent(event);
                          setIsAddModalOpen(true);
                        }}
                      >
                        <FaEdit />
                      </button>
                      <button
                        type="button"
                        className="admin-events__action-btn admin-events__action-btn--danger"
                        title="Delete"
                        aria-label="Delete"
                        onClick={() => handleDelete(event.id)}
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="admin-events__empty">
              <FaCalendarAlt />
              <p>No events found</p>
            </div>
          )}
        </div>
      </div>

      <ViewEventModal
        event={viewEvent as ViewEventRecord | null}
        isOpen={viewEvent != null}
        onClose={() => setViewEvent(null)}
      />

      <AddEventModal
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setEditEvent(null);
        }}
        onSuccess={handleEventCreatedOrUpdated}
        editingEventId={editEvent?.id ?? null}
        initialEvent={editEvent ?? null}
      />
    </AdminLayout>
  );
};
